/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import {
  HTMLAttributes,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'preact/compat';

import {
  Container,
  toLanguageTag,
  SlotProps,
  SlotMethod,
  Slot,
  ResolveImageUrlOptions,
} from '@adobe-commerce/elsie/lib';

import {
  Button,
  Icon,
  ButtonProps,
  Image,
  Incrementer,
  Breadcrumbs,
} from '@adobe-commerce/elsie/components';
import {
  Product,
  Swatches,
  PriceRange,
  Option,
  GiftCardOptions,
  DownloadableOptions,
} from '@/pdp/components';
import { config, fetchProductData } from '@/pdp/api';
import { events } from '@adobe-commerce/event-bus';
import { Lang, useText } from '@adobe-commerce/elsie/i18n';
import { isDateValid, toDateString } from '@/pdp/lib/date';
import {
  setUrlParams,
  getValidatedQuantityFromUrl,
} from '@/pdp/lib/url-params';
import { publishProductPageViewEvent } from '@/pdp/lib/acdl';
import { ProductModel, Option as ProductOption } from '@/pdp/data/models';
import { transformProductData } from '@/pdp/data/transforms/product-transform';

type IconType = keyof typeof import('@adobe-commerce/elsie/icons');

/** @deprecated This type is part of the deprecated ProductDetails component. */
export type Values = {
  sku: string;
  quantity: number;
  optionsUIDs?: string[];
  enteredOptions?: Array<{ uid: string; value: string }>;
};

/** @deprecated This type is part of the deprecated ProductDetails component. */
type DefaultSlotContext = {
  data: ProductModel | null;
  values: Values;
  valid: boolean;
};

/** @deprecated This type is part of the deprecated ProductDetails component. */
export type CarouselConfig = {
  controls?: {
    desktop?: 'thumbnailsRow' | 'thumbnailsColumn' | 'dots' | null;
    mobile?: 'dots' | 'thumbnailsRow' | null;
  };
  arrowsOnMainImage?: boolean;
  loopable?: boolean;
  peak?: {
    desktop: boolean;
    mobile: boolean;
  };
  gap?: 'small' | 'medium' | 'large' | null;
  thumbnailsLoadingMode?: 'lazy' | 'eager';
  imageParams?: ResolveImageUrlOptions;
  thumbnailParams?: ResolveImageUrlOptions;
};

/** @deprecated This type is part of the deprecated ProductDetails component. */
export type OptionsConfig = {
  anchorOptions?: string[];
};

/** @deprecated This interface is part of the deprecated ProductDetails component. */
export interface ProductDetailsProps extends HTMLAttributes<HTMLDivElement> {
  sku: string;
  productData?: ProductModel;
  hideSku?: boolean;
  hideQuantity?: boolean;
  hideShortDescription?: boolean;
  hideDescription?: boolean;
  hideAttributes?: boolean;
  hideSelectedOptionValue?: boolean;
  hideURLParams?: boolean;
  slots?: {
    Title?: SlotProps<DefaultSlotContext>;

    SKU?: SlotProps<DefaultSlotContext>;

    RegularPrice?: SlotProps<DefaultSlotContext>;

    SpecialPrice?: SlotProps<DefaultSlotContext>;

    Options?: SlotProps<DefaultSlotContext>;

    Quantity?: SlotProps<DefaultSlotContext>;

    Actions?: SlotProps<
      DefaultSlotContext & {
        appendButton: SlotMethod<
          Omit<ButtonProps, 'icon'> & {
            text?: string;
            icon?: IconType;
          }
        >;
      }
    >;

    ShortDescription?: SlotProps<DefaultSlotContext>;

    Description?: SlotProps<DefaultSlotContext>;

    Attributes?: SlotProps<DefaultSlotContext>;

    Breadcrumbs?: SlotProps<
      DefaultSlotContext & {
        setSeparator: SlotMethod<IconType>;

        appendLink: SlotMethod<
          HTMLAttributes<HTMLAnchorElement> & { text?: string }
        >;

        appendHTMLElement: SlotMethod<HTMLElement>;
      }
    >;

    GalleryContent?: SlotProps<DefaultSlotContext>;

    InfoContent?: SlotProps<DefaultSlotContext>;

    Content?: SlotProps<DefaultSlotContext>;
  };
  carousel?: CarouselConfig;
  optionsConfig?: OptionsConfig;
  useACDL?: boolean;
  onAddToCart?: (values: Values) => void;
  zoomType?: 'zoom' | 'overlay';
  closeButton?: boolean;
  disableDropdownPreselection?: boolean;
}

/**
 * @deprecated This container has been deprecated and will be removed in a future release.
 * Please use the different composable containers to build your product details page.xs
 */
export const ProductDetails: Container<
  ProductDetailsProps,
  ProductModel | null
> = ({
  sku,
  hideSku,
  hideQuantity,
  hideShortDescription,
  hideDescription,
  hideAttributes,
  hideURLParams,
  hideSelectedOptionValue,
  slots,
  children,
  initialData,
  carousel,
  optionsConfig,
  useACDL,
  onAddToCart,
  zoomType,
  closeButton = true,
  disableDropdownPreselection,
  ...props
}) => {
  // i18n
  const AddToCartLabel = useText('PDP.Product.AddToCart.label').label;
  const IncrementerLabel = useText('PDP.Product.Incrementer.label').label;
  const DetailsLabel = useText('PDP.Product.Details.label').label;
  const ChooseOptionLabel = useText('PDP.Swatches.ChooseOption.label').label;

  // TODO: Investigate if the "|| 'en-US' " is really necessary
  const [locale, setLocale] = useState<Lang>(
    (config.getConfig().defaultLocale as Lang) || 'en-US'
  );

  const [selectionsToUpdate, setSelectionsToUpdate] = useState<Option[]>();

  const [data = null, setData] = useState(initialData);

  const [values, setValues] = useState<Values>(() => {
    const initialQuantity = getValidatedQuantityFromUrl(1); // Default quantity to 1

    const defaultValues: Values = {
      sku,
      quantity: initialQuantity,
    };

    if (data?.optionUIDs) {
      defaultValues.optionsUIDs = data.optionUIDs;
    }
    return defaultValues;
  });

  // the context is invalid for a configurable product that doesn't have all the options selected
  const [valid, setValid] = useState(() => {
    return !(
      data?.options &&
      data?.options?.length > 0 &&
      data?.optionUIDs?.length !== data?.options.length
    );
  });
  const defaultSlotContext: DefaultSlotContext = { data, values, valid };

  function hasProductViewOptionValueProduct(options: ProductOption[]) {
    return options?.some(
      (option) => option?.typename === 'ProductViewOptionValueProduct'
    );
  }

  useEffect(() => {
    const dataEvent = events.on('pdp/data', (payload) => {
      if (payload) {
        setData(payload);
        // Preserve the current quantity value when updating from pdp/data event
        setValues((prev: Values) => ({
          ...prev,
          sku: payload.sku,
          optionsUIDs: payload.optionUIDs,
        }));
      }
    });

    // @ts-ignore - Type error with event bus events
    const updateDataEvent = events.on(
      'pdp/setValues',
      (payload: Partial<Values>) => {
        setValues((prev: Values) => ({ ...prev, ...payload }));
      }
    );

    return () => {
      updateDataEvent?.off();
      dataEvent?.off();
    };
  }, []);

  // Locale
  useEffect(() => {
    const localeEvent = events.on('locale', (payload: string) => {
      if (payload === locale) return;

      setLocale(payload as Lang);

      fetchProductData(sku, {
        preselectFirstOption: !disableDropdownPreselection,
        anchors: optionsConfig?.anchorOptions,
      }).then(setData);
    });
    return () => {
      localeEvent?.off();
    };
  }, [disableDropdownPreselection, locale, optionsConfig?.anchorOptions, sku]);

  useEffect(() => {
    if (useACDL && initialData && Object.keys(initialData).length > 0) {
      publishProductPageViewEvent(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Callbacks
  const handleOnValues = useCallback((newValues: Partial<Values>) => {
    setValues((prev) => ({ ...prev, ...newValues }));
  }, []);

  useEffect(() => {
    events.emit('pdp/values', values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(values)]);

  const handleOptionSelected = useCallback(
    async (selections: { [id: string]: { label: string; value: string } }) => {
      // options Selected
      let optionsUIDs = Object.entries(selections)
        .filter(([_, value]) => value.label !== ChooseOptionLabel) // eslint-disable-line no-unused-vars
        .map(([id, _]) => selections[id].value) // eslint-disable-line no-unused-vars
        .filter((element) => element !== undefined);

      // set optionUIDs to URL params
      // ?optionsUIDs=1,2,3
      // only set the URL params if the hideURLParams is not set to true and the product is not a bundle type
      if (
        !hideURLParams &&
        data?.options &&
        !hasProductViewOptionValueProduct(data.options)
      ) {
        setUrlParams({ optionsUIDs: optionsUIDs.join(',') });
      }

      // update state
      handleOnValues({ optionsUIDs });
      setValid(optionsUIDs?.length === data?.options?.length);

      // get refined product data and update state
      const next = await fetchProductData(sku, {
        anchors: optionsConfig?.anchorOptions,
        isBundle: data?.isBundle,
        optionsUIDs,
        preselectFirstOption: !disableDropdownPreselection,
      });

      setData(next);

      // special processing for anchor options (options that should not be refined when other options are selected)
      if (optionsConfig?.anchorOptions?.length) {
        processUpdates(next, optionsUIDs);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sku]
  );

  const processUpdates = useCallback(
    (next: ProductModel | null, previousOptionsUIDs: string[]) => {
      const updatedOptionsUIDs = next?.optionUIDs ?? [];
      const haveSameValues = previousOptionsUIDs.every((item) =>
        updatedOptionsUIDs.includes(item)
      );
      if (!haveSameValues) {
        setSelectionsToUpdate(next?.options);
        handleOnValues({ optionsUIDs: updatedOptionsUIDs });
        setValid(updatedOptionsUIDs?.length === next?.options?.length);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sku]
  );

  const handleOnErrors = useCallback((_errors: { [id: string]: string }) => {
    if (Object.keys(_errors).length !== 0) setValid(false);
  }, []);

  // Product Attributes
  const attributes = useMemo(() => {
    return data?.attributes?.map(({ label, value, id }, key: number) => {
      // Return empty string for gift card attributes
      if (id === 'ac_giftcard') {
        return '';
      }

      const __html = isDateValid(value)
        ? toDateString(value, locale)
        : value.toString();

      return (
        <li key={key}>
          {/* eslint-disable-next-line react/no-danger */}
          {label}: <span dangerouslySetInnerHTML={{ __html }} />
        </li>
      );
    });
  }, [data?.attributes, locale]);

  const quantity =
    !hideQuantity && data?.inStock ? (
      <Slot
        name="Quantity"
        slot={slots?.Quantity}
        context={{ ...defaultSlotContext }}
      >
        <Incrementer
          key="quantity"
          name="quantity"
          defaultValue={values.quantity.toString()}
          value={values.quantity.toString()}
          min={1}
          aria-label={IncrementerLabel}
          className="pdp-product__quantity"
          onValue={(quantity) => {
            const quantityValue = Number(quantity);
            handleOnValues({ quantity: quantityValue });

            // Add quantity to URL parameters if not set to hide
            if (!hideURLParams && data?.options) {
              // Only update URL if the product is not a bundle type
              if (!hasProductViewOptionValueProduct(data.options)) {
                setUrlParams({ quantity: quantityValue });
              }
            }
          }}
        />
      </Slot>
    ) : undefined;

  const renderOptions = () => {
    if (data?.options) {
      return (
        <Slot
          name="Options"
          slot={slots?.Options}
          context={{ ...defaultSlotContext }}
        >
          <Swatches
            selectedUIDs={values.optionsUIDs}
            options={data.options}
            defaultOptions={values.optionsUIDs}
            selectionsToUpdate={selectionsToUpdate}
            hideSelectedValue={hideSelectedOptionValue}
            disablePreselections={disableDropdownPreselection}
            onValues={handleOptionSelected}
            onErrors={handleOnErrors}
          />
        </Slot>
      );
    }
  };

  const renderGiftCardOptions = () => {
    if (data?.attributes) {
      const giftCardAttributes = data.attributes.filter(
        (attr) => attr.id === 'ac_giftcard'
      );

      if (giftCardAttributes.length > 0) {
        const giftCardElements = giftCardAttributes.map(
          (giftCardAttr, index) => {
            try {
              const giftCardData = JSON.parse(giftCardAttr.value);
              const prevOptions = values.enteredOptions || [];
              const optionValues =
                giftCardData.options?.map((option: any) => {
                  const existingOption = prevOptions.find(
                    (opt: { uid: string; value: string }) =>
                      opt.uid === option.uid
                  );
                  return {
                    id: option.uid,
                    value: existingOption?.value || '',
                  };
                }) || [];

              return (
                <GiftCardOptions
                  key={`giftcard-${index}`}
                  options={giftCardData.options || []}
                  optionValues={optionValues}
                  onOptionChange={(uid: string, value: string) => {
                    const prevOptions = values.enteredOptions || [];
                    const idx = prevOptions.findIndex(
                      (field: { uid: string; value: string }) =>
                        field.uid === uid
                    );
                    let newEnteredOptions;
                    if (idx !== -1) {
                      newEnteredOptions = prevOptions.map(
                        (field: { uid: string; value: string }) =>
                          field.uid === uid ? { ...field, value } : field
                      );
                    } else {
                      newEnteredOptions = [...prevOptions, { uid, value }];
                    }

                    handleOnValues({ enteredOptions: newEnteredOptions });

                    if (data) {
                      const updatedData = { ...data };
                      const giftCardAttributes =
                        updatedData.attributes?.map((attr) => {
                          if (attr.id === 'ac_giftcard') {
                            try {
                              const giftCardData = JSON.parse(attr.value);
                              const updatedOptions =
                                giftCardData.options?.map((option: any) => {
                                  if (option.uid === uid) {
                                    return { ...option, value };
                                  }
                                  return option;
                                }) || [];
                              return {
                                ...attr,
                                value: JSON.stringify({
                                  ...giftCardData,
                                  options: updatedOptions,
                                }),
                              };
                            } catch (error) {
                              return attr;
                            }
                          }
                          return attr;
                        }) || [];
                      updatedData.attributes = giftCardAttributes;
                      setData(updatedData);
                    }
                  }}
                  onValidationChange={(valid: boolean) => {
                    if (!valid) {
                      handleOnErrors({ giftCard: 'Invalid gift card data' });
                    }

                    setValid(valid);
                  }}
                  className="pdp-product__gift-card-options"
                />
              );
            } catch (error) {
              console.warn('Invalid gift card data format:', error);
              return null;
            }
          }
        );

        // Filter out null values and return a single element
        const validElements = giftCardElements.filter(
          (element) => element !== null
        );
        return validElements.length > 0 ? (
          <div className="pdp-product__gift-card-options-container">
            {validElements}
          </div>
        ) : undefined;
      }
    }
    return undefined;
  };

  const renderDownloadableOptions = () => {
    if (data?.attributes) {
      const downloadableAttributes = data.attributes.filter(
        (attr) =>
          attr.id === 'ac_downloadable' &&
          attr.roles?.includes('visible_in_pdp')
      );

      if (downloadableAttributes.length > 0) {
        const downloadableElements = downloadableAttributes.map(
          (downloadableAttr, index) => {
            try {
              const downloadableValue =
                typeof downloadableAttr.value === 'string'
                  ? JSON.parse(downloadableAttr.value)
                  : downloadableAttr.value;

              return (
                <DownloadableOptions
                  key={`downloadable-${index}`}
                  label={downloadableAttr.label}
                  value={downloadableValue}
                  onLinkChange={(uid: string, selected: boolean) => {
                    const prevUIDs = values.optionsUIDs || [];
                    let newOptionsUIDs;

                    if (selected) {
                      if (!prevUIDs.includes(uid)) {
                        newOptionsUIDs = [...prevUIDs, uid];
                      } else {
                        newOptionsUIDs = prevUIDs;
                      }
                    } else {
                      newOptionsUIDs = prevUIDs.filter(
                        (id: string) => id !== uid
                      );
                    }

                    handleOnValues({ optionsUIDs: newOptionsUIDs });
                  }}
                  currency={data.prices?.final?.currency}
                  className="pdp-product__downloadable-options"
                />
              );
            } catch (error) {
              console.warn('Invalid downloadable data format:', error);
              return null;
            }
          }
        );

        // Filter out null values and return a wrapper div
        const validElements = downloadableElements.filter(
          (element) => element !== null
        );
        return validElements.length > 0 ? (
          <div className="pdp-product__downloadable-options-container">
            {validElements}
          </div>
        ) : undefined;
      }
    }
    return undefined;
  };

  return (
    <div {...props}>
      <Product
        title={
          <Slot
            name="Title"
            slot={slots?.Title}
            context={{ ...defaultSlotContext }}
          >
            {data?.name}
          </Slot>
        }
        sku={
          !hideSku ? (
            <Slot
              name="SKU"
              slot={slots?.SKU}
              context={{ ...defaultSlotContext }}
            >
              {data?.sku}
            </Slot>
          ) : undefined
        }
        options={renderOptions()}
        giftCardOptions={renderGiftCardOptions()}
        downloadableOptions={renderDownloadableOptions()}
        breadcrumbs={
          slots?.Breadcrumbs && (
            <Slot
              name="Breadcrumbs"
              slot={slots?.Breadcrumbs}
              context={{
                ...defaultSlotContext,

                setSeparator(callback) {
                  this._registerMethod((...attrs) => {
                    const nextSeparator = callback(...attrs);

                    const separator = nextSeparator && (
                      <Icon source={nextSeparator} />
                    );

                    this._setProps((prev: any) => {
                      return {
                        ...prev,
                        separator,
                        children: [
                          // eslint-disable-next-line react/jsx-key
                          <Breadcrumbs
                            separator={separator}
                            categories={prev.categories}
                          />,
                        ],
                      };
                    });
                  });
                },

                appendLink(callback) {
                  this._registerMethod((...attrs) => {
                    const { text, ...p } = callback(...attrs);

                    const link = <a {...p}>{text}</a>;

                    this._setProps((prev: any) => {
                      const categories = [...(prev.categories || []), link];
                      return {
                        ...prev,
                        categories,
                        children: [
                          // eslint-disable-next-line react/jsx-key
                          <Breadcrumbs
                            separator={prev.separator}
                            categories={categories}
                          />,
                        ],
                      };
                    });
                  });
                },

                appendHTMLElement(callback) {
                  this._registerMethod((...attrs) => {
                    const domElement = callback(...attrs);

                    const vnode = this._htmlElementToVNode(domElement);

                    this._setProps((prev: any) => {
                      const categories = [...(prev.categories || []), vnode];

                      return {
                        ...prev,
                        categories,
                        children: [
                          // eslint-disable-next-line react/jsx-key
                          <Breadcrumbs
                            separator={prev.separator}
                            categories={categories}
                          />,
                        ],
                      };
                    });
                  });
                },
              }}
            />
          )
        }
        quantity={quantity}
        actions={
          <Slot
            name="Actions"
            slot={slots?.Actions}
            context={{
              ...defaultSlotContext,

              appendButton(callback) {
                this._registerMethod((...attrs) => {
                  const _button = callback(...attrs);

                  if (!_button) return;

                  const { text, icon, ...buttonProps } = _button;

                  const button = (
                    <Button
                      type="button"
                      {...buttonProps}
                      icon={icon && <Icon source={icon} />}
                    >
                      {text}
                    </Button>
                  );

                  this._setProps((prev: any) => ({
                    children: [...(prev.children || []), button],
                  }));
                });
              },
            }}
          >
            {
              // Default Add to Cart button if no slot is provided
              !slots?.Actions && (
                <Button
                  size="medium"
                  type="submit"
                  icon={<Icon source="Cart" />}
                  disabled={!data?.inStock || !valid}
                  aria-label={AddToCartLabel}
                  onClick={() => {
                    return onAddToCart?.(values);
                  }}
                >
                  {AddToCartLabel}
                </Button>
              )
            }
          </Slot>
        }
        shortDescription={
          !hideShortDescription ? (
            <Slot
              name="ShortDescription"
              slot={slots?.ShortDescription}
              context={{ ...defaultSlotContext }}
            >
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: data?.shortDescription ?? '',
                }}
              />
            </Slot>
          ) : undefined
        }
        description={
          !hideDescription ? (
            <Slot
              name="Description"
              slot={slots?.Description}
              context={{ ...defaultSlotContext }}
            >
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: data?.description ?? '' }}
              />
            </Slot>
          ) : undefined
        }
        images={
          data?.images?.map(({ label, url }) => (
            <Image
              key={url}
              title={label}
              alt={label}
              src={url}
              // TODO: change width/height for image aspect ratio
              // pending https://jira.corp.adobe.com/browse/DATA-5122
              width={carousel?.imageParams?.width ?? 960}
              height={carousel?.imageParams?.height ?? 1191}
              params={carousel?.imageParams ?? { width: 960 }}
            />
          )) ?? []
        }
        specialPrice={
          data?.prices?.visible ? (
            <Slot
              name="SpecialPrice"
              slot={slots?.SpecialPrice}
              context={{ ...defaultSlotContext }}
            >
              <PriceRange
                {...data.prices?.final}
                locale={toLanguageTag(locale)}
              />
            </Slot>
          ) : undefined
        }
        price={
          data?.prices?.visible &&
          data?.prices?.regular &&
          data?.prices?.final?.amount !== data?.prices?.regular?.amount ? (
            <Slot
              name="RegularPrice"
              slot={slots?.RegularPrice}
              context={{ ...defaultSlotContext }}
            >
              <PriceRange
                {...data.prices?.regular}
                locale={toLanguageTag(locale)}
              />
            </Slot>
          ) : undefined
        }
        zoomType={zoomType}
        closeButton={closeButton}
        carouselConfig={{
          ...carousel,
          thumbnails:
            carousel && Object.keys(carousel).length
              ? data?.images?.map(({ label, url }) => (
                  <Image
                    key={url}
                    title={label}
                    alt={label}
                    src={url}
                    // TODO: change width/height for image aspect ratio
                    // pending https://jira.corp.adobe.com/browse/DATA-5122
                    width={carousel?.thumbnailParams?.width ?? 200}
                    height={carousel?.thumbnailParams?.height ?? 248}
                    params={carousel?.thumbnailParams ?? { width: 200 }}
                  />
                )) ?? []
              : [],
        }}
        outOfStock={!data?.inStock}
        attributes={
          !hideAttributes ? (
            <Slot
              name="Attributes"
              slot={slots?.Attributes}
              context={{ ...defaultSlotContext }}
            >
              {attributes?.length ? (
                <>
                  {DetailsLabel}
                  <ul>{attributes}</ul>
                </>
              ) : null}
            </Slot>
          ) : undefined
        }
        galleryContent={
          slots?.GalleryContent && (
            <Slot
              name="GalleryContent"
              slot={slots.GalleryContent}
              context={{ ...defaultSlotContext }}
            />
          )
        }
        infoContent={
          slots?.InfoContent && (
            <Slot
              name="InfoContent"
              slot={slots.InfoContent}
              context={{ ...defaultSlotContext }}
            />
          )
        }
        productContent={
          slots?.Content && (
            <Slot
              name="Content"
              slot={slots.Content}
              context={{ ...defaultSlotContext }}
            />
          )
        }
      />
    </div>
  );
};

/** @deprecated This method is part of the deprecated ProductDetails component. */
ProductDetails.getInitialData = async function ({
  sku,
  disableDropdownPreselection = false,
  optionsConfig,
}) {
  const initialData = config.getConfig().models?.ProductDetails?.initialData;

  return initialData
    ? transformProductData(initialData)
    : fetchProductData(sku, {
        preselectFirstOption: !disableDropdownPreselection,
        anchors: optionsConfig?.anchorOptions,
      });
};
